import React, { useState } from "react";
import { Button, Card, Form, Container, Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { addLabs } from "../../../actions/labsAction";
import FullPageLoader from "../../../components/FullPageLoader/FullPageLoader";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import SweetAlert from "react-bootstrap-sweetalert";

const PAYMENT_TYPES = ["Self Pay", "Insurance"];
const LAB_TYPES = ["At Home", "In Person"];
const LAB_CATEGORIES = ["STD Tests", "General Health", "Hormonal Tests", "Panels"];

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .min(2, "Title must have at least 2 characters")
    .max(100, "Title can't be longer than 100 characters")
    .trim()
    .required("Title is required"),
  paymentType: Yup.string()
    .oneOf(PAYMENT_TYPES, "Invalid payment type")
    .required("Payment Type is required"),
  type: Yup.string().required("Service Type is required"),
  category: Yup.string().required("Category is required"),
  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be zero or greater"),
  discount: Yup.number().min(0, "Discount must be zero or greater"),
});

const AddLabs = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [getFile, setFile] = useState("");
  const [uploadImage, setUploadImage] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      paymentType: "",
      type: "",
      category: "",
      shortDescription: "",
      description: "",
      photo: "",
      price: "",
      discount: "0",
      status: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("paymentType", values.paymentType);
      formData.append("type", values.type);
      formData.append("category", values.category);
      formData.append("shortDescription", values.shortDescription);
      formData.append("description", values.description);
      formData.append("price", values.price);
      formData.append("discount", values.discount);
      formData.append("status", values.status);
      if (values.photo) formData.append("photo", values.photo);
      setLoader(true);
      let res = await addLabs(formData);
      setLoader(false);
      if (res?.data?.success) {
        navigate("/forms/labs");
        toast.success(res.data.message);
      }
    },
  });

  const handlePreviewImage = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;
    const imageType = file.type.split("/")[1];
    const allowed = ["png", "jpg", "jpeg", "gif", "webp"];
    if (allowed.includes(imageType)) {
      setFieldValue("photo", file);
      setFile(URL.createObjectURL(file));
    } else {
      setUploadImage(true);
      e.target.value = null;
    }
  };

  const hideModel = () => setUploadImage(false);

  const onEditorChange = (event, editor, setFieldValue) => {
    setFieldValue("description", editor.getData());
  };

  return (
    <>
      {uploadImage ? (
        <SweetAlert
          warning
          showCloseButton
          confirmBtnText="OK"
          onConfirm={hideModel}
          confirmBtnBsStyle="danger"
          title="Failed! Please select correct Image Format"
          onCancel={hideModel}
          focusCancelBtn
          customClass="del-sweet-alert"
        >
          Supported files: png, jpg, jpeg, gif, webp
        </SweetAlert>
      ) : null}
      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3 cms-updated-style">
          <Container fluid>
            <Row>
              <Col md="12">
                <Card className="filter-card card">
                  <Card.Header>
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">
                        Add Lab Test
                      </Card.Title>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col>
                        <Form onSubmit={formik.handleSubmit}>

                          {/* Title */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Title <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="title"
                              placeholder="Enter lab test title"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.title}
                            />
                            {formik.touched.title && formik.errors.title && (
                              <label className="pl-1 text-danger">{formik.errors.title}</label>
                            )}
                          </Form.Group>

                          {/* Payment Type (Self Pay / Insurance) */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Payment Type <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              name="paymentType"
                              value={formik.values.paymentType}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            >
                              <option value="">Select Payment Type</option>
                              {PAYMENT_TYPES.map((p) => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </Form.Control>
                            {formik.touched.paymentType && formik.errors.paymentType && (
                              <label className="pl-1 text-danger">{formik.errors.paymentType}</label>
                            )}
                          </Form.Group>

                          {/* Service Type (At Home / In Person) */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Service Type <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              name="type"
                              value={formik.values.type}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            >
                              <option value="">Select Service Type</option>
                              {LAB_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t === "At Home" ? "At-Home Kit" : t}
                                </option>
                              ))}
                            </Form.Control>
                            {formik.touched.type && formik.errors.type && (
                              <label className="pl-1 text-danger">{formik.errors.type}</label>
                            )}
                          </Form.Group>

                          {/* Category (Sub Type) */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Category <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              name="category"
                              value={formik.values.category}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            >
                              <option value="">Select Category</option>
                              {LAB_CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </Form.Control>
                            {formik.touched.category && formik.errors.category && (
                              <label className="pl-1 text-danger">{formik.errors.category}</label>
                            )}
                          </Form.Group>

                          {/* Short Description */}
                          <Form.Group className="mb-3">
                            <Form.Label>Short Description</Form.Label>
                            <Form.Control
                              type="text"
                              name="shortDescription"
                              placeholder="Enter short description"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.shortDescription}
                            />
                          </Form.Group>

                          {/* Description */}
                          <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <CKEditor
                              editor={ClassicEditor}
                              data={formik.values.description || ""}
                              onChange={(event, editor) =>
                                onEditorChange(event, editor, formik.setFieldValue)
                              }
                            />
                          </Form.Group>

                          {/* Image */}
                          {getFile && (
                            <Form.Group className="mb-3">
                              <img src={getFile} alt="preview" width="200" height="200" />
                            </Form.Group>
                          )}
                          <Form.Group
                            className="mb-3"
                            onChange={(e) => handlePreviewImage(e, formik.setFieldValue)}
                          >
                            <Form.Label>Upload Image</Form.Label>
                            <Form.Control
                              type="file"
                              name="photo"
                              accept=".png,.jpg,.jpeg,.webp"
                            />
                          </Form.Group>

                          {/* Price */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Price ($) <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="price"
                              placeholder="0.00"
                              onBlur={formik.handleBlur}
                              onChange={formik.handleChange}
                              value={formik.values.price}
                            />
                            {formik.touched.price && formik.errors.price && (
                              <label className="pl-1 text-danger">{formik.errors.price}</label>
                            )}
                          </Form.Group>

                          {/* Discount */}
                          <Form.Group className="mb-3">
                            <Form.Label>Discount on Price</Form.Label>
                            <Form.Control
                              type="number"
                              name="discount"
                              placeholder="0"
                              onBlur={formik.handleBlur}
                              onChange={formik.handleChange}
                              value={formik.values.discount}
                            />
                            {formik.touched.discount && formik.errors.discount && (
                              <label className="pl-1 text-danger">{formik.errors.discount}</label>
                            )}
                          </Form.Group>

                          {/* Status */}
                          <Row className="mb-3">
                            <Col md={1}>
                              <label className="label-font">Status</label>
                            </Col>
                            <Col md={11} className="check-inline d-flex mt-3 flex-column">
                              <label className="right-label-radio mr-3 mb-2">
                                Active
                                <input
                                  name="status"
                                  className="status"
                                  checked={formik.values.status}
                                  type="radio"
                                  onChange={() => formik.setFieldValue("status", true)}
                                />
                                <span className="checkmark"></span>
                              </label>
                              <label className="right-label-radio mr-3 mb-2">
                                Inactive
                                <input
                                  name="status1"
                                  className="status"
                                  checked={!formik.values.status}
                                  type="radio"
                                  onChange={() => formik.setFieldValue("status", false)}
                                />
                                <span className="checkmark"></span>
                              </label>
                            </Col>
                          </Row>

                          <Button className="btn-filled" type="submit">
                            Submit
                          </Button>
                        </Form>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default AddLabs;
